import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { inr, imgUrl, categoryLabel, HOTEL_CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchBar } from "@/components/SearchBar";
import { MapPin, Star, BadgePercent, SlidersHorizontal } from "lucide-react";

const FALLBACK_IMG = "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(sp.get("category") || "all");
  const [priceRange, setPriceRange] = useState([Number(sp.get("min_price")) || 0, Number(sp.get("max_price")) || 50000]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sp.get("q")) params.q = sp.get("q");
      if (sp.get("city")) params.city = sp.get("city");
      if (sp.get("category")) params.category = sp.get("category");
      if (sp.get("min_price")) params.min_price = sp.get("min_price");
      if (sp.get("max_price")) params.max_price = sp.get("max_price");
      const { data } = await api.get("/hotels", { params });
      setHotels(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHotels(); }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    const newSp = new URLSearchParams(sp.toString());
    if (category && category !== "all") newSp.set("category", category); else newSp.delete("category");
    if (priceRange[0] > 0) newSp.set("min_price", String(priceRange[0])); else newSp.delete("min_price");
    if (priceRange[1] < 50000) newSp.set("max_price", String(priceRange[1])); else newSp.delete("max_price");
    setSp(newSp);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-4">
        <SearchBar />
      </div>

      {/* Secondary refine filters */}
      <div className="flex items-center gap-3 mb-8">
        <Button type="button" variant="outline" size="sm" data-testid="search-toggle-filters" onClick={() => setShowFilters((v) => !v)} className="rounded-full text-xs gap-1.5">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
        </Button>
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border border-[#EBEBEB] rounded-2xl px-4 py-3 shadow-sm flex-1">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="search-category-select" className="rounded-xl sm:w-44">
                <SelectValue placeholder="Any type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="search-category-all">All types</SelectItem>
                {HOTEL_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`search-category-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1 min-w-[180px]">
              <div className="flex justify-between text-xs text-[#717171] mb-1">
                <span>{inr(priceRange[0])}</span>
                <span>{inr(priceRange[1])}</span>
              </div>
              <Slider data-testid="search-price-slider" min={0} max={50000} step={500} value={priceRange} onValueChange={setPriceRange} />
            </div>
            <Button type="button" size="sm" onClick={applyFilters} data-testid="search-apply-filters-btn" className="rounded-full bg-[#222] hover:bg-black text-white text-xs">Apply</Button>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-bold">
          {loading ? "Searching stays…" : `${hotels.length} stays ${(sp.get("q") || sp.get("city")) ? `in ${sp.get("q") || sp.get("city")}` : "available"}`}
        </h1>
        <p className="text-[#717171] text-sm">Zero-commission · transparent prices</p>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`skeleton-${i}`}>
              <Skeleton className="aspect-[4/3] rounded-2xl mb-3" />
              <Skeleton className="h-4 w-2/3 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : hotels.length === 0 ? (
        <div className="text-center py-20 text-[#717171]">
          <p className="text-lg mb-2">No stays found.</p>
          <p className="text-sm">Try a different city or clear filters. Hosts — list your property to be the first!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map((h) => (
            <button
              key={h.id}
              data-testid={`hotel-card-${h.id}`}
              onClick={() => navigate(`/hotel/${h.id}`)}
              className="text-left group"
            >
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-3 bg-[#F0F0F4]">
                <img
                  src={h.images?.[0] ? imgUrl(h.images[0]) : FALLBACK_IMG}
                  alt={h.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white text-[10px] font-bold text-[#FF385C] shadow-sm flex items-center gap-1">
                  <BadgePercent className="w-3 h-3" /> 0% commission
                </div>
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-[10px] font-semibold text-white">
                  {categoryLabel(h.category)}
                </div>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-[#222] truncate">{h.name}</h3>
                  <p className="text-sm text-[#717171] flex items-center gap-1 truncate">
                    <MapPin className="w-3.5 h-3.5 shrink-0" /> {h.city}, {h.country}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-sm shrink-0">
                  <Star className="w-3.5 h-3.5 fill-current text-[#FFB400]" />
                  <span className="font-semibold">{h.star_rating || 4}</span>
                </div>
              </div>
              <div className="mt-2 text-sm">
                {h.min_price ? (
                  <>
                    <span className="font-display font-bold text-base">{inr(h.min_price)}</span>
                    <span className="text-[#717171]"> / night</span>
                    <span className="block text-[11px] font-medium text-[#008489] mt-0.5" data-testid={`hotel-savings-${h.id}`}>
                      You save {inr(Math.round(h.min_price * 0.18))} vs. other travel sites
                    </span>
                  </>
                ) : (
                  <span className="text-[#717171] italic">No rooms yet</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
